/* Core & libraries */
import { useEffect, useState, useMemo, useRef } from 'react';
import { useTheme } from 'react-jss';
import { Field } from 'formik';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import {
  isValidPhoneNumber,
  getCountryCallingCode,
  AsYouType,
} from 'libphonenumber-js';

/* Helpers, constants, custom methods, internal libraries */
import { useGeneralService } from '../../services/generalService';
import { AIRLINES } from '../../constants/appConstants';

/* Components */
import UCSelect from '../ucselect';

/* Styles */
import styles, { countrySelectStyles } from './styles.js';

const messages = defineMessages({
  countryCodeAriaLabel: { id: 'settings.countryCodeAriaLabel' },
});

const AA_COUNTRIES = ['US', 'CA', 'MX', 'BR', 'CL', 'PE', 'CO', 'AR'];
const PRIORITY_COUNTRIES = ['US', 'CA'];

const buildOption = (code, label) => {
  try {
    const dialCode = getCountryCallingCode(code);
    return { value: code, label, countryPhoneCode: `+${dialCode}`, selectedLabel: code };
  } catch {
    return null;
  }
};

const sortByPriority = (options) => options.sort((a, b) => {
  const aIdx = PRIORITY_COUNTRIES.indexOf(a.value);
  const bIdx = PRIORITY_COUNTRIES.indexOf(b.value);
  if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
  if (aIdx !== -1) return -1;
  if (bIdx !== -1) return 1;
  return 0;
});

const UCPhoneCountrySelector = (props) => {
  const { errors, touched, providerCode, values, disabled = false } = props;
  const { getPhoneCodes } = useGeneralService();
  const { formatMessage } = useIntl();

  const [apiCodes, setApiCodes] = useState([]);

  const theme = useTheme();
  const classes = styles({ theme, isDisabled: disabled });

  const formatPhoneDisplay = (countryCode, phoneDigits) => {
    const dial = getCountryCallingCode(countryCode);
    if (!phoneDigits) return `+${dial} `;
    const formatter = new AsYouType(countryCode);
    const formatted = formatter.input(phoneDigits);
    return `+${dial} ${formatted}`;
  };

  const getMaxDigits = (countryCode) => {
    const maxLengths = { US: 10, CA: 10, MX: 10, BR: 11, CL: 9, PE: 9, CO: 10, AR: 11 };
    return maxLengths[countryCode] || 15;
  };

  const rawDigitsRef = useRef(values.phoneNumber || '');

  const [displayPhone, setDisplayPhone] = useState(() => {
    try {
      const country = (values.phoneCountryCode || 'US').toUpperCase();
      return formatPhoneDisplay(country, values.phoneNumber || '');
    } catch {
      return values.phoneNumber || '';
    }
  });

  useEffect(() => {
    let mounted = true;
    const fallback = [{ countryCode: 'US', countryName: formatMessage({ id: 'phoneCountries.us' }) }];

    (async () => {
      try {
        const res = await getPhoneCodes();
        if (mounted) {
          setApiCodes(Array.isArray(res) && res.length > 0 ? res : fallback);
        }
      } catch {
        if (mounted) setApiCodes(fallback);
      }
    })();

    return () => { mounted = false; };
  }, []);

  const countries = useMemo(() => {
    if (providerCode === AIRLINES.AA) {
      const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
      return AA_COUNTRIES.map((code) => buildOption(code, countryNames.of(code))).filter(Boolean);
    }

    const options = apiCodes.map((c) => buildOption(c.countryCode.toUpperCase(), c.countryName)).filter(Boolean);
    return sortByPriority(options);
  }, [providerCode, apiCodes]);

  if (providerCode !== AIRLINES.AA && apiCodes.length === 0) {
    return null;
  }

  const phoneValidation = (values) => {
    if (!values.phoneNumber) return undefined;
    const phoneValid = isValidPhoneNumber(values.phoneNumber, values.phoneCountryCode);
    return phoneValid ? undefined : <FormattedMessage id="tphoneValidation.notValid" />;
  };

  const hasError =
    (errors.phoneNumber && touched.phoneNumber) ||
    (errors.phoneCountryCode && touched.phoneCountryCode);

  const selectedCountry = (values.phoneCountryCode || 'US').toUpperCase();

  return (
    <div className={`${classes.phoneInputWrapper} ${hasError ? classes.inputError : ''}`}>
      <Field name="phoneNumber" validate={() => phoneValidation(values)}>
        {({ field, form }) => (
          <>
            <UCSelect
              className={classes.countrySelect}
              styles={countrySelectStyles(theme)}
              options={countries}
              value={selectedCountry}
              disabled={disabled}
              ariaLabel={formatMessage(messages.countryCodeAriaLabel)}
              onChange={(selected) => {
                const code = selected.value;
                form.setFieldValue('phoneCountryCode', code, false);
                form.setFieldValue('phoneNumber', '', false);
                rawDigitsRef.current = '';
                setDisplayPhone(formatPhoneDisplay(code, ''));
                setTimeout(() => form.validateField('phoneNumber'), 0);
              }}
              customSelectedValue="selectedLabel"
              isSearchable={true}
              getOptionLabel={(data) => (
                <div>{data.label} <span>{data.countryPhoneCode}</span></div>
              )}
              filterOption={(option, inputValue) => {
                const { label, value } = option.data;
                return (
                  label.toLowerCase().includes(inputValue.toLowerCase()) ||
                  value.toLowerCase().includes(inputValue.toLowerCase())
                );
              }}
            />
            <input
              className={classes.phoneInput}
              type="tel"
              value={displayPhone}
              disabled={disabled}
              onKeyDown={(e) => {
                if (e.key === 'Backspace') {
                  e.preventDefault();
                  const current = rawDigitsRef.current;
                  if (current.length > 0) {
                    const newDigits = current.slice(0, -1);
                    rawDigitsRef.current = newDigits;
                    setDisplayPhone(formatPhoneDisplay(selectedCountry, newDigits));
                    form.setFieldValue('phoneNumber', newDigits, false);
                    form.setFieldTouched('phoneNumber', true, false);
                    setTimeout(() => form.validateField('phoneNumber'), 0);
                  }
                }
              }}
              onChange={(e) => {
                const newRaw = e.target.value.replace(/\D/g, '');
                const dial = String(getCountryCallingCode(selectedCountry));
                const digitsAfterDial = newRaw.startsWith(dial) ? newRaw.slice(dial.length) : newRaw;
                const digits = digitsAfterDial.slice(0, getMaxDigits(selectedCountry));
                rawDigitsRef.current = digits;
                setDisplayPhone(formatPhoneDisplay(selectedCountry, digits));
                form.setFieldValue('phoneNumber', digits, false);
                form.setFieldTouched('phoneNumber', true, false);
                setTimeout(() => form.validateField('phoneNumber'), 0);
              }}
              onBlur={() => form.setFieldTouched('phoneNumber', true)}
            />
          </>
        )}
      </Field>
    </div>
  );
};

export default UCPhoneCountrySelector;
